import Swiper from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import debounce from "debounce";

/**
 * Calculate slide widths based on container size and layout
 * @param {HTMLElement} carousel - The swiper carousel element
 * @param {string} layout - Layout type: 'full', 'boxed', or 'nested'
 */
function calculateSlideWidths(carousel, layout) {
  const spacer = carousel.closest('.versatile-carousel').querySelector('.versatile-carousel__spacer');
  if (!spacer) return;

  let containerWidth = spacer.offsetWidth;
  const windowWidth = window.innerWidth;

  // Don't calculate for mobile (below 1024px)
  if (windowWidth < 1024) return;

  // Determine spacing based on screen size
  let spaceBetween = 20;
  if (windowWidth >= 1680) {
    spaceBetween = 32;
  } else if (windowWidth >= 1280) {
    spaceBetween = 24;
  }

  // Get the size class from swiper-wrapper
  const swiperWrapper = carousel.querySelector('.swiper-wrapper');
  const sizeClass = Array.from(swiperWrapper.classList).find(cls => cls.startsWith('size-'));
  const slidesPerContainer = sizeClass ? parseInt(sizeClass.replace('size-', '')) : 4;

  // Adjust container width for nested layout
  if (layout === "nested") {
    const nestedOffset = getNestedOffset(carousel);
    containerWidth -= nestedOffset * 2;
  }

  // Calculate slide width
  const totalSpacing = spaceBetween * (slidesPerContainer - 1);
  const availableWidth = containerWidth - totalSpacing;
  const slideWidth = availableWidth / slidesPerContainer;
  
  // Calculate featured width (double width + spacing)
  const featuredWidth = (slideWidth * 2) + spaceBetween;

  // Set CSS custom properties
  carousel.style.setProperty('--slide-width', `${slideWidth}px`);
  carousel.style.setProperty('--featured-width', `${featuredWidth}px`);
  carousel.style.setProperty('--slides-per-view', slidesPerContainer.toString());
}

/**
 * Calculate nested offset for nested layout
 * @param {HTMLElement} carousel - The swiper carousel element
 * @returns {number} The nested offset value
 */
function getNestedOffset(carousel) {
  const spacer = carousel.closest('.versatile-carousel').querySelector('.versatile-carousel__spacer');
  if (!spacer) return 64;
  return spacer.offsetWidth / 24;
}

/**
 * Get layout type from wrapper classes
 * @param {HTMLElement} wrapper - The carousel wrapper element
 * @returns {string} Layout type
 */
function getLayoutType(wrapper) {
  if (wrapper.classList.contains("boxed")) {
    return "boxed";
  } else if (wrapper.classList.contains("nested")) {
    return "nested";
  }
  return "full";
}

/**
 * Get slides offset based on layout and screen size
 * @param {string} layout - Layout type
 * @param {number} nestedOffset - Nested offset value
 * @param {number} windowWidth - Current window width
 * @returns {number} Slides offset value
 */
function getSlidesOffset(layout, nestedOffset, windowWidth) {
  if (layout === "boxed") {
    return 0;
  } else if (layout === "nested") {
    return windowWidth >= 1280 ? nestedOffset : 20;
  }
  return windowWidth >= 1280 ? 144 : 20;
}

document.addEventListener("DOMContentLoaded", () => {
  const carousels = document.querySelectorAll(".versatile-carousel .swiper");

  carousels.forEach(carousel => {
    const wrapper = carousel.closest(".versatile-carousel__wrapper");
    const layout = getLayoutType(wrapper);
    const nestedOffset = layout === "nested" ? getNestedOffset(carousel) : 0;

    const swiper = new Swiper(carousel, {
      modules: [Navigation, Pagination],
      spaceBetween: 20,
      slidesPerView: "auto",
      slidesOffsetBefore: getSlidesOffset(layout, nestedOffset, window.innerWidth),
      slidesOffsetAfter: getSlidesOffset(layout, nestedOffset, window.innerWidth),
      navigation: {
        nextEl: wrapper.querySelector(".swiper-button-next"),
        prevEl: wrapper.querySelector(".swiper-button-prev"),
      },
      pagination: {
        el: wrapper.querySelector(".swiper-pagination"),
        clickable: true,
      },
      breakpoints: {
        1280: {
          spaceBetween: 24,
          slidesOffsetBefore: getSlidesOffset(layout, nestedOffset, 1280),
          slidesOffsetAfter: getSlidesOffset(layout, nestedOffset, 1280),
        },
        1680: {
          spaceBetween: 32,
          slidesOffsetBefore: getSlidesOffset(layout, nestedOffset, 1680),
          slidesOffsetAfter: getSlidesOffset(layout, nestedOffset, 1680),
        },
      },
      on: {
        init: function() {
          calculateSlideWidths(carousel, layout);
        },
        resize: function() {
          calculateSlideWidths(carousel, layout);

          // Update nested offset for nested layout
          if (layout === "nested") {
            const newOffset = getNestedOffset(carousel);
            if (window.innerWidth >= 1280) {
              this.params.slidesOffsetBefore = newOffset;
              this.params.slidesOffsetAfter = newOffset;
              this.update();
            }
          }
        }
      }
    });

    // Debounced resize handler
    const debouncedResize = debounce(() => {
      swiper.update();
      swiper.updateSize();
      swiper.updateSlides();
      swiper.updateProgress();
      swiper.updateSlidesClasses();
      calculateSlideWidths(carousel, layout);
    }, 100);

    // Event listeners
    window.addEventListener('resize', debouncedResize);
    window.addEventListener('orientationchange', debouncedResize);
    
    // ResizeObserver for better performance
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(debouncedResize);
      resizeObserver.observe(carousel);
    }
  });
});
